import { Fragment, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@monksflow/monks-ui';
import type { PerformanceScope } from '../model/scope-params';

export interface ScopeBreadcrumbProps {
  scope: PerformanceScope;
  campaignName?: string;
  adsetName?: string;
  onWiden: (scope: PerformanceScope) => void;
}

interface Crumb {
  key: string;
  label: string;
  target: PerformanceScope;
}

const ROOT_SCOPE: PerformanceScope = { campaignId: null, adsetId: null };

/**
 * Narrows every performance view to all campaigns, one campaign, or one ad set. Ancestor
 * levels are clickable - clicking widens the scope via `onWiden` rather than navigating a
 * URL, since the scope lives alongside other search params `useScope` already merges. The
 * current (deepest) level renders as `BreadcrumbPage`, not a link: it names where the user
 * already is, so an interactive control there would be a no-op control read out to a
 * screen-reader user as if it did something.
 *
 * Nothing renders at project scope. A lone root crumb has no ancestor to widen to, so it
 * would be dead text inside a navigation landmark on every first load - the state almost
 * every visit is in. Deciding that here rather than at each call site keeps the rule with
 * the component that owns the crumbs.
 */
export function ScopeBreadcrumb({ scope, campaignName, adsetName, onWiden }: ScopeBreadcrumbProps) {
  const { t } = useTranslation('projects');
  const anchorRef = useRef<HTMLDivElement>(null);

  /**
   * Widening destroys the control that was just activated: the crumb becomes the current
   * level and stops being a link, and widening all the way to project scope leaves no
   * crumbs at all. Either way the focused element leaves the document and focus falls to
   * `document.body`, which puts a keyboard user back at the top of the page and tells a
   * screen-reader user nothing about where the scope went.
   *
   * The flag survives while project scope does, rather than being cleared once focus has
   * been placed: clearing it is what unmounts the anchor, which would throw the focus away
   * one render after catching it.
   */
  const [isHoldingFocus, setIsHoldingFocus] = useState(false);
  const hasPendingFocus = useRef(false);

  useEffect(() => {
    if (!hasPendingFocus.current) return;
    hasPendingFocus.current = false;
    anchorRef.current?.focus();
  }, [scope.campaignId, scope.adsetId, isHoldingFocus]);

  useEffect(() => {
    // Narrowing again brings the real crumbs back, and they carry the focus from here on.
    if (scope.campaignId) setIsHoldingFocus(false);
  }, [scope.campaignId]);

  const widen = (target: PerformanceScope) => {
    hasPendingFocus.current = true;
    if (!target.campaignId) setIsHoldingFocus(true);
    onWiden(target);
  };

  const crumbs: Crumb[] = [
    { key: 'root', label: t('performanceScope.scopeBreadcrumb.allCampaigns'), target: ROOT_SCOPE },
  ];

  if (scope.campaignId) {
    crumbs.push({
      key: 'campaign',
      label: campaignName ?? scope.campaignId,
      target: { campaignId: scope.campaignId, adsetId: null },
    });

    if (scope.adsetId) {
      crumbs.push({
        key: 'adset',
        label: adsetName ?? scope.adsetId,
        target: scope,
      });
    }
  }

  // Nothing to show at project scope, and nothing rendered either — except after a widen
  // that landed here, where an anchor holds the focus the vanished crumb would otherwise
  // have thrown away. It is `sr-only`, so it takes itself out of flow and adds no gap to the
  // parent's flex column; on a first load, the common case, there is no element at all.
  if (crumbs.length === 1) {
    return isHoldingFocus ? (
      <div
        ref={anchorRef}
        tabIndex={-1}
        className="sr-only"
        role="status"
        data-testid="scope-breadcrumb-anchor"
      >
        {t('performanceScope.scopeBreadcrumb.showingAllCampaigns')}
      </div>
    ) : null;
  }

  const lastIndex = crumbs.length - 1;

  return (
    <div
      ref={anchorRef}
      tabIndex={-1}
      className="outline-none"
      data-testid="scope-breadcrumb-anchor"
    >
      <Breadcrumb>
        <BreadcrumbList>
          {crumbs.map((crumb, index) => {
            const isCurrent = index === lastIndex;
            return (
              <Fragment key={crumb.key}>
                <BreadcrumbItem>
                  {isCurrent ? (
                    <BreadcrumbPage>{crumb.label}</BreadcrumbPage>
                  ) : (
                    <BreadcrumbLink asChild>
                      <button
                        type="button"
                        className="bg-transparent p-0"
                        onClick={() => widen(crumb.target)}
                      >
                        {crumb.label}
                      </button>
                    </BreadcrumbLink>
                  )}
                </BreadcrumbItem>
                {!isCurrent && <BreadcrumbSeparator />}
              </Fragment>
            );
          })}
        </BreadcrumbList>
      </Breadcrumb>
    </div>
  );
}
